import { ofType } from 'redux-observable';
import { catchError, forkJoin, from, map, Observable, switchMap } from 'rxjs';
import { adminSlice } from './index.ts';
import {
  getClinicianInfo,
  getProviderIntegrationInfo,
  getProviderProductOffering,
  getProviderSettingsById,
} from '@ax/service';
import { Auth } from '@ax/auth';

export const onAdminClinicianSearchChanged = (action$: Observable<any>) =>
  action$.pipe(
    ofType(adminSlice.actions.adminClinicianSearchChanged.type),
    switchMap((action) => {
      const resultClinician$ = from(getClinicianInfo(action.payload.inputValue.toLowerCase()));
      return forkJoin([resultClinician$]).pipe(
        map((clinicianPayload) => {
          return adminSlice.actions.adminClinicianSearchSucceeded({
            result: clinicianPayload[0],
          });
        }),
        catchError(() => {
          return [adminSlice.actions.adminClinicianSearchFailed({ action })];
        }),
      );
    }),
  );

export const onAdminClinicianSelectedRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(adminSlice.actions.adminClinicianSelectedRequested.type),
    switchMap((action) => {
      return [
        adminSlice.actions.adminClinicianSelectedSucceeded({
          selectedClinician: action.payload.clinicianSelected,
        }),
        adminSlice.actions.adminConnectedClinicianLoadRequested({
          providerId: Number(action.payload.clinicianSelected.id),
        }),
      ];
    }),
  );

export const onAdminConnectedClinicianLoadRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(adminSlice.actions.adminConnectedClinicianLoadRequested.type),
    switchMap((action) => {
      try {
        return forkJoin({
          providerSettings: from(getProviderSettingsById(action.payload.providerId)).pipe(
            map((response) => ({ data: response, error: null, errorMessage: '' })),
            catchError((error) => [
              {
                error,
                errorMessage:
                  'An error occurred while retrieving the settings for the current user',
              },
            ]),
          ),
          // TODO: Remove this.
          providerProductOffering: from(getProviderProductOffering()).pipe(
            map((response) => ({ data: response, error: null, errorMessage: '' })),
            catchError((error) => [
              {
                error,
                errorMessage:
                  'An error occurred while retrieving the product offering for the current user',
              },
            ]),
          ),
          providerIntegrationInfo: from(getProviderIntegrationInfo(Auth.currentUser().uid)).pipe(
            map((response) => ({ data: response, error: null, errorMessage: '' })),
            catchError((error) => [
              {
                error,
                errorMessage:
                  'An error occurred while retrieving the integration info for the current user',
              },
            ]),
          ),
        }).pipe(
          map((responses) => {
            const errors = Object.values(responses).filter((response) => response.error);
            if (errors.length) {
              return adminSlice.actions.adminConnectedClinicianLoadFailed({
                errors: errors.map((error) => ({
                  error: error.error,
                  message: error.errorMessage,
                })),
              });
            }
            return adminSlice.actions.adminConnectedClinicianLoadSucceeded(responses);
          }),
          catchError((error) => {
            return [
              adminSlice.actions.adminConnectedClinicianLoadFailed({
                errors: [
                  {
                    error,
                    message: 'An unknown error occurred while initializing the provider state',
                  },
                ],
              }),
            ];
          }),
        );
      } catch (error) {
        return [
          adminSlice.actions.adminConnectedClinicianLoadFailed({
            errors: [
              { error, message: 'An unknown error occurred while initializing the provider state' },
            ],
          }),
        ];
      }
    }),
  );

export default [
  onAdminClinicianSearchChanged,
  onAdminClinicianSelectedRequested,
  onAdminConnectedClinicianLoadRequested,
];
